import { supabase } from "@/lib/supabaseClient";

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  role: "user" | "admin";
  plan: "free" | "trial" | "premium" | "expired";
  is_active: boolean;
  
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  subscription_started_at: string | null;
  subscription_expires_at: string | null;
  
  trial_tests_limit: number;
  trial_tests_used: number;
  
  // Future Payment Gateways placeholders
  payment_provider: string | null;
  payment_customer_id: string | null;
  subscription_id: string | null;
  subscription_status: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
}

export interface PlatformStats {
  totalUsers: number;
  activeUsers: number;
  trialFreeUsers: number;
  trialUsers: number;
  premiumUsers: number;
  expiredUsers: number;
  premiumWaitlist: number;
  testsCompleted: number;
  writingCorrections: number;
  questionsTotal: number;
  flashcardsTotal: number;
  errorsTotal: number;
}

export const profileService = {
  /**
   * Fetches the user profile. If it doesn't exist yet, lazily creates it.
   * This guarantees seamless backward compatibility for older accounts.
   */
  async fetchOrCreateProfile(userId: string, email: string, name?: string): Promise<UserProfile | null> {
    if (!userId || !email) return null;
    if (!supabase) {
      console.warn("[ProfileService] Supabase client is not configured.");
      return null;
    }

    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      let existingProfile = null;
      let fetchError = null;

      // 1. Attempt to fetch existing profile
      try {
        const result = await supabase
          .from("user_profiles")
          .select("*")
          .eq("id", userId)
          .maybeSingle();
        existingProfile = result.data;
        fetchError = result.error;
      } catch (e: any) {
        fetchError = e;
      }

      const now = new Date().toISOString();

      if (existingProfile && !fetchError) {
        // Update last login at
        try {
          await supabase
            .from("user_profiles")
            .update({ last_login_at: now })
            .eq("id", userId);
        } catch (e) {
          // Ignore error updating last login if table missing or RLS blocked
        }
          
        return {
          ...existingProfile,
          last_login_at: now
        } as UserProfile;
      }

      if (fetchError) {
        console.warn("[ProfileService] Could not fetch profile (possibly missing table or RLS):", fetchError.message || fetchError);
      }

      // 2. Profile doesn't exist or table is missing, create a fallback profile (in-memory or lazy migration)
      console.log("[ProfileService] Creating fallback/lazy profile for user", userId);
      
      let adminMatch = null;
      try {
        // Check if user is registered as administrator in the legacy admin_users table
        const { data } = await supabase
          .from("admin_users")
          .select("id")
          .eq("email", normalizedEmail)
          .maybeSingle();
        adminMatch = data;
      } catch (e) {
        console.warn("[ProfileService] Could not check admin_users table.");
      }

      const calculatedRole = adminMatch ? "admin" : "user";
      const fallbackName = name || email.split("@")[0];

      const newProfile: Partial<UserProfile> = {
        id: userId,
        email: normalizedEmail,
        name: fallbackName,
        role: calculatedRole,
        plan: calculatedRole === "admin" ? "premium" : "free",
        is_active: true,
        last_login_at: now,
        trial_tests_limit: 4,
        trial_tests_used: 0
      };

      if (!fetchError) {
        // Only try to insert if the fetch didn't hard-fail on missing table, 
        // to avoid noisy insert errors on missing tables.
        try {
          const { data: createdProfile, error: insertError } = await supabase
            .from("user_profiles")
            .insert([newProfile])
            .select("*")
            .single();

          if (!insertError && createdProfile) {
            return createdProfile as UserProfile;
          } else {
             console.warn("[ProfileService] Error inserting lazy profile:", insertError);
          }
        } catch (e) {
          console.warn("[ProfileService] Exception inserting lazy profile:", e);
        }
      }

      // Return the in-memory fallback profile so the app doesn't crash
      console.log("[ProfileService] Returning in-memory fallback profile.");
      return newProfile as UserProfile;
    } catch (err) {
      console.error("[ProfileService] Unexpected failure to fetch or create user profile:", err);
      // Even in the worst case, we can return a basic mock profile so the app loads
      const fallbackRole = "user";
      return {
        id: userId,
        email: email,
        name: name || email.split("@")[0],
        role: fallbackRole,
        plan: "free",
        is_active: true,
        last_login_at: new Date().toISOString(),
        trial_tests_limit: 4,
        trial_tests_used: 0
      } as UserProfile;
    }
  },

  /**
   * Updates standard profile details (e.g. from Settings or Onboarding)
   */
  async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile | null> {
    if (!userId || !supabase) return null;

    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .update(updates)
        .eq("id", userId)
        .select("*")
        .single();

      if (error) throw error;
      return data as UserProfile;
    } catch (err) {
      console.error("[ProfileService] Error updating user profile:", err);
      return null;
    }
  },

  /**
   * Safe increment count for trial tests used
   */
  async incrementTrialTestsUsed(userId: string): Promise<UserProfile | null> {
    if (!userId || !supabase) return null;

    try {
      // Get current count
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("trial_tests_used")
        .eq("id", userId)
        .single();

      const currentUsed = profile?.trial_tests_used || 0;

      const { data, error } = await supabase
        .from("user_profiles")
        .update({ trial_tests_used: currentUsed + 1 })
        .eq("id", userId)
        .select("*")
        .single();

      if (error) throw error;
      
      // Dispatch custom event to notify components that usage updated
      window.dispatchEvent(new Event("userProfileUpdated"));

      return data as UserProfile;
    } catch (err) {
      console.error("[ProfileService] Error incrementing trial tests count:", err);
      return null;
    }
  },

  /**
   * Adds a user to the premium interest waitlist with LocalStorage fallback.
   */
  async addToWaitlist(userId: string, email: string, source: string = "premium_page", planInterest?: string): Promise<boolean> {
    const normalizedEmail = email.toLowerCase().trim();
    const payload: any = {
      user_id: userId || null,
      email: normalizedEmail,
      source: source,
      created_at: new Date().toISOString()
    };

    if (supabase) {
      try {
        const payloadWithPlan = { ...payload, plan_interest: planInterest || null };
        const { error } = await supabase.from("premium_waitlist").insert([payloadWithPlan]);
        if (!error) {
          return true;
        }

        // Catch column mismatch error and retry without it
        if (
          error.message.includes("plan_interest") || 
          error.code === "P0002" || 
          error.message.includes("column") ||
          error.message.includes("does not exist")
        ) {
          console.warn("[ProfileService] plan_interest column might not exist, retrying insert without it...");
          const { error: retryError } = await supabase.from("premium_waitlist").insert([payload]);
          if (!retryError) return true;
        }

        console.warn("[ProfileService] Error inserting waitlist record in supabase, attempting local fallback:", error.message);
      } catch (err) {
        console.warn("[ProfileService] Unexpected error inserting waitlist record in supabase:", err);
      }
    }

    // Local storage fallback
    try {
      const existing = localStorage.getItem("tef_premium_waitlist_registered");
      const list = existing ? JSON.parse(existing) : [];
      list.push({ ...payload, plan_interest: planInterest || null });
      localStorage.setItem("tef_premium_waitlist_registered", JSON.stringify(list));
      return true;
    } catch (err) {
      console.error("[ProfileService] LocalStorage fallback for waitlist failed:", err);
      return false;
    }
  },

  /**
   * Administrative List of all user profiles
   */
  async listAllProfiles(): Promise<UserProfile[]> {
    if (!supabase) return [];

    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as UserProfile[];
    } catch (err) {
      console.error("[ProfileService] Error listing profiles:", err);
      return [];
    }
  },

  /**
   * Administrative override for user configurations
   */
  async updateProfileByAdmin(userId: string, updates: Partial<UserProfile>): Promise<UserProfile | null> {
    if (!userId || !supabase) return null;

    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .update(updates)
        .eq("id", userId)
        .select("*")
        .single();

      if (error) throw error;
      return data as UserProfile;
    } catch (err) {
      console.error("[ProfileService] Error administrative override on user profile:", err);
      return null;
    }
  },

  /**
   * Administrative fetch of Platform KPIs
   */
  async fetchPlatformStats(): Promise<PlatformStats> {
    const defaultStats: PlatformStats = {
      totalUsers: 0,
      activeUsers: 0,
      trialFreeUsers: 0,
      trialUsers: 0,
      premiumUsers: 0,
      expiredUsers: 0,
      premiumWaitlist: 0,
      testsCompleted: 0,
      writingCorrections: 0,
      questionsTotal: 0,
      flashcardsTotal: 0,
      errorsTotal: 0
    };

    if (!supabase) return defaultStats;

    try {
      // Helper to fetch count securely and efficiently
      const getCount = async (table: string, filterCallback?: (query: any) => any): Promise<number> => {
        let baseQuery = supabase.from(table).select("*", { count: "exact", head: true });
        if (filterCallback) {
          baseQuery = filterCallback(baseQuery);
        }
        const { count, error } = await baseQuery;
        if (error) {
          console.error(`[ProfileService] Error getting count for ${table}:`, error);
          return 0;
        }
        return count || 0;
      };

      const [
        totalUsers,
        activeUsers,
        trialFreeUsers,
        trialUsers,
        premiumUsers,
        expiredUsers,
        premiumWaitlist,
        testsCompleted,
        writingCorrections,
        questionsTotal,
        flashcardsTotal,
        errorsTotal
      ] = await Promise.all([
        getCount("user_profiles"),
        getCount("user_profiles", q => q.eq("is_active", true)),
        getCount("user_profiles", q => q.in("plan", ["free", "trial"])),
        getCount("user_profiles", q => q.eq("plan", "trial")),
        getCount("user_profiles", q => q.eq("plan", "premium")),
        getCount("user_profiles", q => q.eq("plan", "expired")),
        getCount("premium_waitlist"),
        getCount("test_history"),
        getCount("test_history", q => q.eq("competence", "Expression Écrite (EE)")),
        getCount("questions"),
        getCount("flashcards"),
        getCount("error_bank")
      ]);

      return {
        totalUsers,
        activeUsers,
        trialFreeUsers,
        trialUsers,
        premiumUsers,
        expiredUsers,
        premiumWaitlist,
        testsCompleted,
        writingCorrections,
        questionsTotal,
        flashcardsTotal,
        errorsTotal
      };
    } catch (err) {
      console.error("[ProfileService] Unexpected error querying platform stats:", err);
      return defaultStats;
    }
  }
};
